import React, { useCallback, useEffect } from 'react';

import { useStoreContext } from 'features/store';
import { useIntl } from 'react-intl';

import { IMainMenuViewOption } from '@rbi-ctg/menu';
import { Helmet } from 'components/helmet';
import { usePullToReload } from 'components/pull-to-reload';
import { useRumPageView } from 'hooks/rum/use-rum-page-view';
import { useMenuScroll } from 'hooks/use-menu-scroll';
import { useFocusEffect } from 'navigation/hooks/use-focus-effect';
import { useRoute } from 'navigation/hooks/use-route';
import MenuTileGrid from 'pages/menu/components/menu-tile-grid';
import { MenuSkeletonLoader } from 'pages/menu/components/menu-tile-grid/menu-tile-grid-loading';
import { useMenuContext } from 'state/menu';
import { useMainMenuContext } from 'state/menu/main-menu';
import { ServiceMode, useServiceModeContext } from 'state/service-mode';
import { EventName, emitEvent } from 'utils/event-hub';

import { MenuReorderWidget } from '../components/menu-reorder-widget';
import withMenuEmptyState from '../components/with-menu-empty-state';
import { useFetchMainMenu, useFetchMainMenuOnMount } from '../hooks/use-fetch-main-menu';

import { MenuEmptyState } from './menu-empty-state';
import { excludeDisabledMenuSections } from './utils';

const waitForDuration = async (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

const MainMenuComponent = () => {
  useRumPageView('main-menu', 'Main Menu');
  const { data, getPricingAndAvailability, isMenuLoading } = useMainMenuContext();
  const fetchMainMenu = useFetchMainMenu();

  const { formatMessage } = useIntl();

  const { serviceMode, setOrderBay } = useServiceModeContext();
  const {
    activeDayParts,
    allPricingDependenciesAreSettled,
    forceRefreshActiveDayParts,
    showBurgersForBreakfast,
    hasError,
  } = useMenuContext();

  const { params } = useRoute<{ identifier: string }>();

  useEffect(() => {
    // If an 'identifier' is present in this path and customer is on Curbside
    // service mode, assume the customer is in the Order Bay flow
    if (serviceMode === ServiceMode.CURBSIDE && params.identifier) {
      setOrderBay(params.identifier);
    }
  }, [serviceMode, setOrderBay, params.identifier]);

  const menuData = excludeDisabledMenuSections({
    menuData: data?.Menu?.options as IMainMenuViewOption[] | undefined,
    activeDayParts,
    showBurgersForBreakfast,
    serviceMode,
    getPricingAndAvailability,
  });

  const onRefresh = async (): Promise<void> => {
    // This is just an animation feedback of one second
    await waitForDuration(1000);
    forceRefreshActiveDayParts();
    fetchMainMenu(true);
  };

  useMenuScroll();

  useFocusEffect(
    useCallback(() => {
      emitEvent(EventName.SCREEN_RENDER, {
        screenId: 'menu',
      });
    }, [])
  );

  const { isPosDataLoading } = useStoreContext();
  const isFetchMainMenuPending = useFetchMainMenuOnMount();
  const loading = isFetchMainMenuPending || isMenuLoading || isPosDataLoading;

  const hasMenuData = !!menuData?.length && allPricingDependenciesAreSettled;

  const { refreshControl } = usePullToReload({
    onRefresh,
    isDisabled: loading,
  });

  const menuContent =
    hasMenuData && !hasError ? (
      <MenuTileGrid data={menuData} refreshControl={refreshControl} />
    ) : (
      <MenuEmptyState logWhenTrue={!loading} />
    );

  return (
    <>
      <Helmet title={formatMessage({ id: 'mainMenu' })} />
      {loading ? (
        <MenuSkeletonLoader />
      ) : (
        <>
          <MenuReorderWidget />
          {menuContent}
        </>
      )}
    </>
  );
};

// MainMenu.whyDidYouRender = true;
export const MainMenuPage = withMenuEmptyState(MainMenuComponent);
